export type PreviewLocale = 'en' | 'ar';

export interface PreviewDocumentData {
  header?: string;
  body?: string;
  imageUrl?: string;
  locale: PreviewLocale;
  publishedAt?: string;
}

const WORDS_PER_MINUTE = 200;
const FONTS_PATH = '/fonts/blog-preview';

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

export function readingTimeOf(body?: string): number {
  const text = stripHtml(body || '');
  return Math.max(1, Math.round((text ? text.split(' ').length : 0) / WORDS_PER_MINUTE));
}

export function formatPreviewDate(locale: PreviewLocale, publishedAt?: string): string {
  const date = publishedAt ? new Date(publishedAt) : new Date();
  const safeDate = isNaN(date.getTime()) ? new Date() : date;

  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-EG' : 'en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).format(safeDate);
}

const previewStyles = `
  @font-face {
    font-family: 'Recoleta';
    src: url('${FONTS_PATH}/recoleta-regular.otf') format('opentype');
    font-display: swap;
  }
  @font-face {
    font-family: 'Gilroy';
    src: url('${FONTS_PATH}/Gilroy-Regular.woff') format('woff');
    font-weight: 400;
    font-display: swap;
  }
  @font-face {
    font-family: 'Gilroy';
    src: url('${FONTS_PATH}/Gilroy-Bold.woff') format('woff');
    font-weight: 700;
    font-display: swap;
  }

  * { box-sizing: border-box; }

  body {
    margin: 0;
    padding: 40px 40px 64px;
    background: #faf8f6;
    font-family: 'Gilroy', sans-serif;
  }

  .article { width: 100%; max-width: 900px; margin: 0 auto; }

  h1 {
    margin: 0;
    font-family: 'Recoleta', serif;
    font-size: 44px;
    line-height: 1.12;
    font-weight: 400;
    letter-spacing: -1.1px;
    color: #292a2b;
  }

  .meta {
    margin-top: 16px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    font-size: 14px;
    color: #707071;
  }

  .meta .dot { color: #aaa5a0; }

  .cover {
    margin-top: 32px;
    overflow: hidden;
    border-radius: 20px;
    background: #f4f0eb;
  }

  .cover img { display: block; width: 100%; height: 440px; object-fit: cover; }

  .article-body {
    margin-top: 40px;
    color: #545555;
    font-size: 17px;
    line-height: 30px;
  }

  .article-body > * + * { margin-top: 24px; }

  .article-body h2,
  .article-body h3,
  .article-body h4 {
    font-family: 'Recoleta', serif;
    color: #292a2b;
    letter-spacing: -0.4px;
    margin-top: 48px;
  }

  .article-body h2 { font-size: 32px; line-height: 1.2; }
  .article-body h3 { font-size: 26px; line-height: 1.25; }
  .article-body h4 { font-size: 21px; line-height: 1.3; }

  .article-body a { color: #207150; text-decoration: underline; text-underline-offset: 3px; }
  .article-body strong { font-weight: 700; color: #292a2b; }

  .article-body ul, .article-body ol { padding-inline-start: 22px; }
  .article-body ul { list-style: disc; }
  .article-body ol { list-style: decimal; }
  .article-body li + li { margin-top: 10px; }

  .article-body img { width: 100%; border-radius: 20px; object-fit: cover; }

  .article-body figure { margin-top: 40px; }
  .article-body figcaption {
    margin-top: 12px;
    font-size: 14px;
    color: #707071;
    text-align: center;
  }

  .article-body blockquote {
    margin: 40px 0 0;
    padding: 8px 0;
    padding-inline-start: 28px;
    border-inline-start: 2px solid #237c58;
    font-family: 'Recoleta', serif;
    font-size: 26px;
    line-height: 1.35;
    color: #292a2b;
    letter-spacing: -0.3px;
  }

  .article-body blockquote p { margin: 0; }

  .article-body hr { margin-top: 40px; border: 0; border-top: 1px solid #d9d3cd; }

  .article-body table { width: 100%; border-collapse: collapse; }
  .article-body th, .article-body td {
    border: 1px solid #d9d3cd;
    padding: 10px 14px;
    text-align: start;
  }
`;

export function buildPreviewDocument({
  header,
  body,
  imageUrl,
  locale,
  publishedAt
}: PreviewDocumentData): string {
  const isArabic = locale === 'ar';
  const minutes = readingTimeOf(body);
  const minRead = isArabic ? `${minutes} دقائق قراءة` : `${minutes} min read`;

  const cover = imageUrl
    ? `<div class="cover"><img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(
        header || ''
      )}" /></div>`
    : '';

  return `<!DOCTYPE html>
<html lang="${isArabic ? 'ar' : 'en'}" dir="${isArabic ? 'rtl' : 'ltr'}">
  <head>
    <meta charset="utf-8" />
    <style>${previewStyles}</style>
  </head>
  <body>
    <article class="article">
      <h1>${escapeHtml(header || '')}</h1>
      <div class="meta">
        <span>${escapeHtml(formatPreviewDate(locale, publishedAt))}</span>
        <span class="dot">&middot;</span>
        <span>${escapeHtml(minRead)}</span>
      </div>
      ${cover}
      <div class="article-body">${body || ''}</div>
    </article>
  </body>
</html>`;
}
